import { useEffect, useRef } from "react";
import * as THREE from "three";

const HEADPHONES_IMAGE_URL = "/images/hlt-talent-headphones.png";

export default function Talent3DStage({ children }) {
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const pointerRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const section = sectionRef.current;
    const canvas = canvasRef.current;
    if (!section || !canvas) return undefined;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);
    camera.position.set(0, 0, 7.1);

    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      canvas,
      powerPreference: "high-performance",
    });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.8));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.18;

    const stage = new THREE.Group();
    const artwork = new THREE.Group();
    stage.add(artwork);
    scene.add(stage);

    const textureLoader = new THREE.TextureLoader();
    const headphonesTexture = textureLoader.load(HEADPHONES_IMAGE_URL);
    headphonesTexture.colorSpace = THREE.SRGBColorSpace;
    headphonesTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();

    const headphones = new THREE.Mesh(
      new THREE.PlaneGeometry(6.65, 3.74, 1, 1),
      new THREE.MeshBasicMaterial({
        map: headphonesTexture,
      })
    );
    headphones.position.z = 0.05;
    artwork.add(headphones);

    const halo = new THREE.Mesh(
      new THREE.PlaneGeometry(6.68, 3.76, 1, 1),
      new THREE.MeshBasicMaterial({
        map: headphonesTexture,
        color: "#d4af37",
        opacity: 0.055,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    halo.position.z = -0.12;
    halo.scale.setScalar(1.03);
    artwork.add(halo);

    let frame = 0;
    const targetRotation = { x: 0, y: -0.035, z: 0 };

    const resize = () => {
      const rect = section.getBoundingClientRect();
      const width = Math.max(1, rect.width);
      const height = Math.max(1, rect.height);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.position.z = width < 720 ? 7.9 : 7.1;
      camera.updateProjectionMatrix();

      if (width < 720) {
        stage.position.set(0.44, -0.34, -0.28);
        stage.scale.setScalar(0.86);
      } else {
        stage.position.set(1.72, 0.05, -0.28);
        stage.scale.setScalar(1.18);
      }
    };

    const onPointerMove = (event) => {
      const rect = section.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
      const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
      pointerRef.current.x = Math.max(-1, Math.min(1, x));
      pointerRef.current.y = Math.max(-1, Math.min(1, y));
      targetRotation.y = -0.035 + pointerRef.current.x * 0.11;
      targetRotation.x = -pointerRef.current.y * 0.04;
      targetRotation.z = pointerRef.current.x * 0.012;
    };

    const onPointerLeave = () => {
      pointerRef.current.x = 0;
      pointerRef.current.y = 0;
      targetRotation.x = 0;
      targetRotation.y = -0.035;
      targetRotation.z = 0;
    };

    const animate = () => {
      frame = window.requestAnimationFrame(animate);
      const time = performance.now() * 0.001;
      const { x, y } = pointerRef.current;

      camera.position.x += (x * 0.22 - camera.position.x) * 0.045;
      camera.position.y += (-y * 0.12 - camera.position.y) * 0.045;
      camera.lookAt(0, 0, 0);

      artwork.rotation.x += (targetRotation.x + Math.sin(time * 0.32) * 0.006 - artwork.rotation.x) * 0.085;
      artwork.rotation.y += (targetRotation.y + Math.sin(time * 0.24) * 0.01 - artwork.rotation.y) * 0.085;
      artwork.rotation.z += (targetRotation.z - artwork.rotation.z) * 0.085;
      artwork.position.y = Math.sin(time * 0.72) * 0.028;
      halo.scale.setScalar(1.03 + Math.sin(time * 1.2) * 0.01);

      stage.position.x += ((window.innerWidth < 720 ? 0.44 : 1.72) + x * 0.05 - stage.position.x) * 0.042;
      stage.position.y += ((window.innerWidth < 720 ? -0.34 : 0.05) - y * 0.045 - stage.position.y) * 0.042;

      renderer.render(scene, camera);
    };

    resize();
    animate();
    section.addEventListener("pointermove", onPointerMove);
    section.addEventListener("pointerleave", onPointerLeave);
    window.addEventListener("resize", resize);

    return () => {
      window.cancelAnimationFrame(frame);
      section.removeEventListener("pointermove", onPointerMove);
      section.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("resize", resize);
      headphonesTexture.dispose();
      scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) object.material.dispose();
      });
      renderer.dispose();
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative min-h-[34rem] overflow-hidden border border-white/10 bg-black">
      <canvas
        ref={canvasRef}
        data-talent-3d-canvas
        aria-hidden="true"
        className="absolute inset-0 h-full w-full"
      />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_71%_42%,rgba(212,175,55,0.08),transparent_35%),linear-gradient(90deg,rgba(0,0,0,0.98),rgba(0,0,0,0.86)_42%,rgba(0,0,0,0.05)_69%,rgba(0,0,0,0.14))]" />
      {children}
    </section>
  );
}
